/**
  * Swiper Slider Custom Element
  * Be sure to inlude the classes "swiper-wrapper" around all the slides and "swiper-slide" on each slide.
  Swiper uses these internally.
 
  Example:
  <swiper-carousel
    data-space-between="0"
    data-slides-per-view="1"
    data-centered="true"
    data-centered-bounds="true"
    class=""
  >
    <div class="swiper-wrapper">
      # for item in iterable
      <div class="swiper-slide"></div>
    </div>
  </swiper-carousel>
*/

import { debounce } from 'theme';

class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    
    this.showPagination = this.dataset.pagination === 'true' ? {
      clickable: true,
      el: '[data-pagination]',
      type: 'bullets',
      bulletActiveClass: `${this.dataset.bulletActiveClass} swiper-pagination-bullet-active` || 'swiper-pagination-bullet-active',
      bulletClass: `${this.dataset.bulletClass} swiper-pagination-bullet` || 'swiper-pagination-bullet',
      dynamicBullets: this.dataset.dynamicBullets === 'true' ?? false,
    } : false;

    this.width = Number(this.dataset.width) || null;
    this.scrollbar = this.dataset.scrollbar === 'true' ? {
      el: '[data-scrollbar]',
      draggable: false,
    } : false;
  }

  connectedCallback() {
    if (window.Swiper) {
      this.init();
      this.addResizeListener();
      return;
    }

    document.addEventListener('DOMContentLoaded', (e) => {
      document.querySelector('[data-swiper-script]').addEventListener('load', () => {
        this.init();
        this.addResizeListener();
      });        
    });
  }

  addResizeListener() {
    window.addEventListener('resize', debounce(() => {
      if (!this.classList.contains('swiper-initialized')) return;
      this.reinit();
    }, 1000));
  }

  reinit() {
    this.slider.destroy();
    this.init();
  }

  equalizeFeaturedLinksSlideHeights() {
    if (!this.classList.contains('featured-links-slider__swiper')) return;

    const slides = Array.from(this.querySelectorAll('.swiper-slide'));
    if (!slides.length) return;

    slides.forEach((slide) => {
      slide.style.height = 'auto';
    });

    const maxHeight = slides.reduce((max, slide) => Math.max(max, slide.offsetHeight), 0);
    if (!maxHeight) return;

    slides.forEach((slide) => {
      slide.style.height = `${maxHeight}px`;
    });
  }

  bindFeaturedLinksImageLoad() {
    if (!this.classList.contains('featured-links-slider__swiper')) return;

    this.querySelectorAll('.swiper-slide img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => this.equalizeFeaturedLinksSlideHeights(), { once: true });
    });
  }

  init() {
    const enableCardsEffect = this.dataset.cardsEffectMobile == 'true';
    const isMobileAtInit = window.innerWidth < 1000;
    const cssWidthMode = this.dataset.cssWidthMode === 'true';
    
    let mobileSlidesPerView = Number(this.dataset.slidesPerViewMobile) || 1;
    let desktopSlidesPerView = Number(this.dataset.slidesPerView) || 1;
    
    if (cssWidthMode) {
      mobileSlidesPerView = 'auto';
      desktopSlidesPerView = 'auto';
    }
    
    const baseEffect = (enableCardsEffect && isMobileAtInit) ? 'cards' : 'slide';
    const centerInsufficientMobile = this.dataset.centerInsufficientMobile === 'true' && isMobileAtInit;

    const breakpointsConfig = {
      1000: {
        spaceBetween: Number(this.dataset.spaceBetween) || 2,
        slidesOffsetAfter: Number(this.dataset.slidesOffsetAfter) || 0,
        slidesOffsetBefore: Number(this.dataset.slidesOffsetBefore) || 0,
        slidesPerView: desktopSlidesPerView,
        slidesPerGroup: Number(this.dataset.slidesPerScroll) || 1,
        effect: 'slide',
        centerInsufficientSlides: !!this.dataset.centered,
      }
    };
    
    const options = {
      autoHeight: this.dataset.autoheight === 'true' ? true : false,
      slidesOffsetAfter: Number(this.dataset.mobileSlidesOffsetAfter) || 0,
      slidesOffsetBefore: Number(this.dataset.mobileSlidesOffsetBefore) || 0,
      touchMoveStopPropagation: true,
      touchStartForcePreventDefault: true,
      grabCursor: true,
      freeMode: this.dataset.freeMode === 'true' ? true : false,
      autoplay: this.dataset.autoplay == 'true' ? { delay: this.dataset.delay || 3000, disableOnInteraction: this.dataset.disableOnInteraction == "true" ? true : false} : false,
      initialSlide: Number(this.dataset.initialSlide) || 0,
      pagination: this.showPagination,
      scrollbar: this.scrollbar,
      spaceBetween: Number(this.dataset.spaceBetweenMobile) || 2,
      preventClicksPropagation: true,
      loop: this.dataset.loop === 'true' ? true : false,
      rewind: this.dataset.rewind === 'true' ? true : false,
      width: this.width || this.parentElement.offsetWidth,
      slidesPerView: mobileSlidesPerView,
      slidesPerGroup: Number(this.dataset.slidesPerScrollMobile) || 1,
      centeredSlides: !!this.dataset.centered,
      centeredSlidesBounds: !!this.dataset.centered,
      centerInsufficientSlides: !!this.dataset.centered || centerInsufficientMobile,
      allowTouchMove: this.dataset.allowTouchMove === 'false' ? false : true,
      draggable: true,
      watchOverflow: true,
      updateOnWindowResize: true,
      effect: baseEffect,
      navigation: {
        nextEl: this.dataset.externalNavButtons ? document.querySelector(`[data-next="${this.dataset.externalNavButtons}"]`) : this.querySelector('[data-next]'),
        prevEl: this.dataset.externalNavButtons ? document.querySelector(`[data-prev="${this.dataset.externalNavButtons}"]`) : this.querySelector('[data-prev]'),
      },
      breakpoints: breakpointsConfig
    };
  
    this.slider = new Swiper(this, options);

    if (this.classList.contains('featured-links-slider__swiper')) {
      requestAnimationFrame(() => {
        this.equalizeFeaturedLinksSlideHeights();
        this.bindFeaturedLinksImageLoad();
      });
    }
  }
}

if (!window.customElements.get('swiper-slider')) {
  window.customElements.define('swiper-slider', SwiperSlider);
}

